import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as faceapi from 'face-api.js';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './card';
import { Button } from './button';
import { Alert, AlertDescription } from './alert';
import { Badge } from './badge';
import { Progress } from './progress';
import { 
  Camera, Check, X, Loader2, RefreshCw, ShieldCheck, 
  ShieldAlert, UserCheck, AlertCircle, Scan, Eye, MoveHorizontal
} from 'lucide-react';
import { toast } from './sonner';

// Face verification configuration
const FACE_CONFIG = {
  MIN_CONFIDENCE: 0.5,           // Minimum face detection confidence
  MATCH_THRESHOLD: 0.5,          // Maximum distance for face match (lower = stricter)
  MODEL_URL: '/models',          // Primary path to face-api.js models
  MODEL_URL_FALLBACK: 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.12/model', // Fallback CDN
  // Liveness detection settings - OPTIMIZED for easier blink detection
  BLINK_THRESHOLD: 0.40,         // Eye aspect ratio threshold for blink detection (higher = easier to detect)
  BLINK_OPEN_THRESHOLD: 0.45,    // Threshold to confirm eye is open again
  BLINK_CONSECUTIVE_FRAMES: 1,   // Number of consecutive frames needed to confirm blink (reduced to 1)
  HEAD_TURN_THRESHOLD: 20,       // Degrees for head turn detection (increased for easier detection)
  HEAD_TURN_MIN_ANGLE: 15,       // Minimum angle to consider head turned
  LIVENESS_TIMEOUT: 30000,       // 30 seconds to complete liveness check
  DETECTION_INTERVAL: 80,        // Detection interval in ms (faster = better blink detection)
  SKIP_DELAY_SECONDS: 5,         // Seconds before skip button appears
  BLINK_FAIL_THRESHOLD: 3,       // Number of failed blink attempts before offering alternative
  BLINK_ATTEMPT_TIMEOUT: 8000,   // Time window to detect blink before counting as failed attempt
};

const FaceVerification = ({ 
  mode = 'verify',               // 'register' or 'verify'
  referenceDescriptor = null,    // Reference face descriptor for verification
  onVerificationComplete,        // Callback when verification completes
  onRegistrationComplete,        // Callback when registration completes
  onCancel,                      // Cancel callback
  currentPhoto = null,           // Current photo to verify (base64)
}) => {
  // Model loading state
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingError, setLoadingError] = useState(null);
  
  // Camera state
  const [stream, setStream] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [facingMode, setFacingMode] = useState('user');
  const [cameraError, setCameraError] = useState(null);  // New: track camera errors
  
  // Face detection state
  const [detecting, setDetecting] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [facePosition, setFacePosition] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  
  // Verification state
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  
  // Liveness detection state
  const [livenessEnabled, setLivenessEnabled] = useState(true);
  const [livenessStep, setLivenessStep] = useState(null); // 'blink', 'head_turn', 'turn_left', 'turn_right', 'complete'
  const [livenessMethod, setLivenessMethod] = useState('head_turn'); // Default: 'head_turn' for better security
  const [livenessProgress, setLivenessProgress] = useState(0);
  const [blinkDetected, setBlinkDetected] = useState(false);
  const [headTurnDetected, setHeadTurnDetected] = useState(false);
  const [turnLeftDetected, setTurnLeftDetected] = useState(false);
  const [turnRightDetected, setTurnRightDetected] = useState(false);
  const [lastEyeState, setLastEyeState] = useState('open'); // 'open' or 'closed'
  const [livenessStartTime, setLivenessStartTime] = useState(null);
  const [headPositions, setHeadPositions] = useState([]); // Track head movement
  const [closedFrameCount, setClosedFrameCount] = useState(0); // Track consecutive closed frames
  const [currentEAR, setCurrentEAR] = useState(0); // Track current EAR for debugging
  const [currentYaw, setCurrentYaw] = useState(0); // Track current head yaw angle
  const [skipCountdown, setSkipCountdown] = useState(null); // Countdown to show skip button
  const [canSkipLiveness, setCanSkipLiveness] = useState(false); // Whether skip button is available
  const [blinkFailCount, setBlinkFailCount] = useState(0); // Track failed blink attempts
  const [showAlternativeOption, setShowAlternativeOption] = useState(false); // Show alternative method option
  const [blinkAttemptStart, setBlinkAttemptStart] = useState(null); // Track blink attempt start time
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const detectionIntervalRef = useRef(null);

  // Load face-api.js models
  useEffect(() => {
    const loadModels = async () => {
      const tryLoadFromUrl = async (modelUrl) => {
        // Load models sequentially with progress updates
        await faceapi.nets.tinyFaceDetector.loadFromUri(modelUrl);
        setLoadingProgress(40);
        
        await faceapi.nets.faceLandmark68Net.loadFromUri(modelUrl);
        setLoadingProgress(70);
        
        await faceapi.nets.faceRecognitionNet.loadFromUri(modelUrl);
        setLoadingProgress(100);
      };

      try {
        setLoadingProgress(10);
        
        // Try loading from local models first
        try {
          console.log('Trying to load face-api.js models from local:', FACE_CONFIG.MODEL_URL);
          await tryLoadFromUrl(FACE_CONFIG.MODEL_URL);
          setModelsLoaded(true);
          console.log('Face-api.js models loaded successfully from local');
          return;
        } catch (localError) {
          console.warn('Failed to load from local, trying CDN fallback:', localError.message);
          setLoadingProgress(10);
        }
        
        // Fallback to CDN
        console.log('Loading face-api.js models from CDN:', FACE_CONFIG.MODEL_URL_FALLBACK);
        await tryLoadFromUrl(FACE_CONFIG.MODEL_URL_FALLBACK);
        setModelsLoaded(true);
        console.log('Face-api.js models loaded successfully from CDN');
        
      } catch (error) {
        console.error('Failed to load face-api.js models:', error);
        setLoadingError('Gagal memuat model pengenalan wajah. Pastikan koneksi internet stabil dan coba refresh halaman.');
      }
    };

    loadModels();

    return () => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
      }
    };
  }, []);

  // Calculate Eye Aspect Ratio (EAR) for blink detection
  // EAR = (|p2-p6| + |p3-p5|) / (2 * |p1-p4|)
  // When eye is open, EAR is high; when closed, EAR is low
  const calculateEAR = useCallback((eyeLandmarks) => {
    if (!eyeLandmarks || eyeLandmarks.length < 6) return 1;
    
    // Eye landmarks: 0=outer corner, 1-2=upper lid, 3=inner corner, 4-5=lower lid
    const p1 = eyeLandmarks[0]; // outer corner
    const p2 = eyeLandmarks[1]; // upper lid outer
    const p3 = eyeLandmarks[2]; // upper lid inner  
    const p4 = eyeLandmarks[3]; // inner corner
    const p5 = eyeLandmarks[4]; // lower lid inner
    const p6 = eyeLandmarks[5]; // lower lid outer
    
    // Calculate vertical distances
    const v1 = Math.sqrt(Math.pow(p2.x - p6.x, 2) + Math.pow(p2.y - p6.y, 2));
    const v2 = Math.sqrt(Math.pow(p3.x - p5.x, 2) + Math.pow(p3.y - p5.y, 2));
    
    // Calculate horizontal distance
    const h = Math.sqrt(Math.pow(p1.x - p4.x, 2) + Math.pow(p1.y - p4.y, 2));
    
    if (h === 0) return 1;
    
    const ear = (v1 + v2) / (2 * h);
    return ear;
  }, []);

  // Calculate head pose (yaw) from face landmarks
  const calculateHeadYaw = useCallback((landmarks) => {
    if (!landmarks) return 0;
    
    const positions = landmarks.positions;
    if (!positions || positions.length < 68) return 0;
    
    // Use nose and face outline to estimate yaw
    // Nose tip: point 30, Left face edge: point 0, Right face edge: point 16
    const noseTip = positions[30];
    const leftEdge = positions[0];
    const rightEdge = positions[16];
    
    // Calculate distances from nose to each edge
    const leftDist = Math.sqrt(Math.pow(noseTip.x - leftEdge.x, 2) + Math.pow(noseTip.y - leftEdge.y, 2));
    const rightDist = Math.sqrt(Math.pow(noseTip.x - rightEdge.x, 2) + Math.pow(noseTip.y - rightEdge.y, 2));
    
    // Calculate ratio - if face is straight, ratio ≈ 1
    // If turned left, ratio < 1; if turned right, ratio > 1
    const ratio = leftDist / rightDist;
    
    // Convert to approximate angle (-30 to +30 degrees)
    const yaw = (ratio - 1) * 45; // Increased sensitivity
    
    return yaw;
  }, []);

  // Switch to head turn method
  const switchToHeadTurnMethod = useCallback(() => {
    setLivenessMethod('head_turn');
    setLivenessStep('turn_left');
    setTurnLeftDetected(false);
    setTurnRightDetected(false);
    setLivenessProgress(0);
    setShowAlternativeOption(false);
    
    toast.info('🔄 Metode Alternatif Aktif', {
      description: 'Tolehkan kepala ke KIRI, lalu ke KANAN',
      duration: 5000
    });
  }, []);

  // Start liveness detection challenge
  const startLivenessCheck = useCallback(() => {
    if (livenessMethod === 'blink') {
      setLivenessStep('blink');
      setBlinkAttemptStart(Date.now());
      toast.info('🔒 Verifikasi Keamanan', {
        description: 'Kedipkan mata Anda secara perlahan untuk verifikasi',
        duration: 5000
      });
    } else {
      setLivenessStep('turn_left');
      toast.info('🔒 Verifikasi Keamanan', {
        description: 'Tolehkan kepala ke KIRI atau KANAN',
        duration: 5000
      });
    }
    
    setLivenessProgress(0);
    setBlinkDetected(false);
    setHeadTurnDetected(false);
    setTurnLeftDetected(false);
    setTurnRightDetected(false);
    setLastEyeState('open');
    setLivenessStartTime(Date.now());
    setHeadPositions([]);
    setClosedFrameCount(0);
    setCurrentEAR(0);
    setCurrentYaw(0);
    setSkipCountdown(FACE_CONFIG.SKIP_DELAY_SECONDS);
    setCanSkipLiveness(false);
  }, []);

  // Skip liveness and proceed directly
  const skipLivenessCheck = useCallback(() => {
    setLivenessStep('complete');
    setLivenessProgress(100);
    setBlinkDetected(false); // Mark as skipped, not detected
    
    toast.warning('Verifikasi liveness dilewati', {
      description: 'Absensi akan dicatat dengan security flag',
      duration: 3000
    });
  }, []);

  // Countdown timer for skip button - works for all liveness methods
  useEffect(() => {
    const activeLivenessSteps = ['blink', 'turn_left', 'turn_right'];
    if (activeLivenessSteps.includes(livenessStep) && skipCountdown !== null && skipCountdown > 0) {
      const timer = setTimeout(() => {
        setSkipCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (skipCountdown === 0) {
      setCanSkipLiveness(true);
    }
  }, [livenessStep, skipCountdown]);

  // Check liveness timeout
  useEffect(() => {
    if (livenessStep && livenessStep !== 'complete' && livenessStartTime) {
      const checkTimeout = setInterval(() => {
        if (Date.now() - livenessStartTime > FACE_CONFIG.LIVENESS_TIMEOUT) {
          setLivenessStep(null);
          setLivenessProgress(0);
          toast.error('Waktu verifikasi habis', {
            description: 'Silakan coba lagi dengan kedipkan mata'
          });
          clearInterval(checkTimeout);
        }
      }, 1000);
      
      return () => clearInterval(checkTimeout);
    }
  }, [livenessStep, livenessStartTime]);

  // Auto-start liveness check when camera is ready and face is detected (for verify mode)
  useEffect(() => {
    if (mode === 'verify' && livenessEnabled && cameraReady && faceDetected && !livenessStep) {
      // Small delay to ensure stable face detection before starting liveness
      const timer = setTimeout(() => {
        startLivenessCheck();
        toast.info('🔒 Verifikasi Anti-Spoofing', {
          description: livenessMethod === 'blink' 
            ? 'Kedipkan mata Anda untuk membuktikan ini bukan foto'
            : 'Tolehkan kepala ke KIRI atau KANAN'
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [mode, livenessEnabled, cameraReady, faceDetected, livenessStep, livenessMethod, startLivenessCheck]);

  // Initialize camera
  const initCamera = useCallback(async () => {
    setCameraError(null);  // Reset error state
    
    try {
      const constraints = {
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 640 },
          height: { ideal: 480 }
        },
        audio: false
      };

      const { getCameraStream } = await import('../../lib/platformUtils');
      const mediaStream = await getCameraStream(constraints);
      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().then(() => {
            setCameraReady(true);
            startFaceDetection();
          });
        };
      }
    } catch (error) {
      console.error('Camera error:', error);
      
      // Determine error type and set appropriate message
      let errorInfo = {
        type: 'unknown',
        title: 'Gagal Mengakses Kamera',
        message: 'Terjadi kesalahan saat mengakses kamera.',
        solution: 'Coba refresh halaman atau gunakan browser lain.'
      };
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorInfo = {
          type: 'permission',
          title: 'Izin Kamera Ditolak',
          message: 'Browser memblokir akses ke kamera perangkat Anda.',
          solution: 'Klik ikon 🔒 di address bar → Izinkan Kamera → Refresh halaman'
        };
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorInfo = {
          type: 'not_found',
          title: 'Kamera Tidak Ditemukan',
          message: 'Tidak ada kamera yang terdeteksi di perangkat ini.',
          solution: 'Pastikan kamera terpasang dan tidak digunakan aplikasi lain.'
        };
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        errorInfo = {
          type: 'in_use',
          title: 'Kamera Sedang Digunakan',
          message: 'Kamera mungkin sedang digunakan oleh aplikasi lain.',
          solution: 'Tutup aplikasi lain yang menggunakan kamera, lalu coba lagi.'
        };
      } else if (error.message === 'BROWSER_NOT_SUPPORTED') {
        errorInfo = {
          type: 'not_supported',
          title: 'Browser Tidak Mendukung',
          message: 'Browser Anda tidak mendukung akses kamera.',
          solution: 'Gunakan Chrome, Firefox, atau Safari versi terbaru.'
        };
      } else if (error.name === 'OverconstrainedError') {
        errorInfo = {
          type: 'constraints',
          title: 'Kamera Tidak Kompatibel',
          message: 'Kamera tidak mendukung pengaturan yang diminta.',
          solution: 'Coba gunakan kamera lain atau refresh halaman.'
        };
      }
      
      setCameraError(errorInfo);
      toast.error(errorInfo.title, {
        description: errorInfo.solution
      });
    }
  }, [facingMode]);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraReady(false);
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }
  }, [stream]);

  // Start face detection loop
  const startFaceDetection = useCallback(() => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }

    detectionIntervalRef.current = setInterval(async () => {
      if (!videoRef.current || !overlayCanvasRef.current || !modelsLoaded) return;

      const video = videoRef.current;
      const overlayCanvas = overlayCanvasRef.current;
      
      // Match canvas size to video
      overlayCanvas.width = video.videoWidth;
      overlayCanvas.height = video.videoHeight;

      try {
        const detections = await faceapi
          .detectAllFaces(video, new faceapi.TinyFaceDetectorOptions({
            inputSize: 320,
            scoreThreshold: FACE_CONFIG.MIN_CONFIDENCE
          }))
          .withFaceLandmarks();

        // Draw detection overlay
        const ctx = overlayCanvas.getContext('2d');
        ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);

        if (detections.length === 1) {
          setFaceDetected(true);
          const box = detections[0].detection.box;
          setFacePosition(box);
          
          // Draw green box for detected face
          ctx.strokeStyle = livenessStep === 'complete' ? '#10b981' : '#3b82f6';
          ctx.lineWidth = 3;
          ctx.strokeRect(box.x, box.y, box.width, box.height);
          
          // Draw face landmark points
          const landmarks = detections[0].landmarks;
          ctx.fillStyle = '#10b981';
          landmarks.positions.forEach(point => {
            ctx.beginPath();
            ctx.arc(point.x, point.y, 2, 0, 2 * Math.PI);
            ctx.fill();
          });
          
          // === LIVENESS DETECTION ===
          if (livenessEnabled && livenessStep && livenessStep !== 'complete') {
            // Get eye landmarks for blink detection
            const leftEye = landmarks.getLeftEye();
            const rightEye = landmarks.getRightEye();
            
            // Calculate EAR for both eyes
            const leftEAR = calculateEAR(leftEye);
            const rightEAR = calculateEAR(rightEye);
            const avgEAR = (leftEAR + rightEAR) / 2;
            
            // Calculate head yaw
            const headYaw = calculateHeadYaw(landmarks);
            
            // Track head positions for movement analysis
            setHeadPositions(prev => {
              const newPositions = [...prev, { yaw: headYaw, timestamp: Date.now() }];
              // Keep last 30 positions (about 6 seconds of data)
              return newPositions.slice(-30);
            });
            
            // Step 1: Blink Detection
            if (livenessStep === 'blink') {
              // Store current EAR for visual feedback
              setCurrentEAR(avgEAR);
              
              // Check for blink attempt timeout - auto switch to head turn after multiple failures
              if (blinkAttemptStart && Date.now() - blinkAttemptStart > FACE_CONFIG.BLINK_ATTEMPT_TIMEOUT) {
                // Time window expired without successful blink
                setBlinkFailCount(prev => {
                  const newCount = prev + 1;
                  if (newCount >= FACE_CONFIG.BLINK_FAIL_THRESHOLD) {
                    // Show alternative option or auto-switch
                    setShowAlternativeOption(true);
                    toast.warning('Kedipan sulit terdeteksi', {
                      description: 'Anda dapat mencoba metode alternatif: tolehkan kepala',
                      duration: 5000
                    });
                  } else {
                    toast.info(`Percobaan ke-${newCount}. Coba kedipkan lebih lama...`, { duration: 3000 });
                  }
                  return newCount;
                });
                // Reset blink attempt timer
                setBlinkAttemptStart(Date.now());
              }
              
              // Update progress based on eye state
              if (avgEAR < FACE_CONFIG.BLINK_THRESHOLD) {
                // Eye is closed/closing
                setClosedFrameCount(prev => prev + 1);
                setLastEyeState('closed');
                
                // Show progress when eye is closing
                if (closedFrameCount >= 1) {
                  setLivenessProgress(50);
                }
              } else if (lastEyeState === 'closed' && avgEAR >= FACE_CONFIG.BLINK_OPEN_THRESHOLD) {
                // Eye opened after being closed - BLINK DETECTED!
                if (closedFrameCount >= FACE_CONFIG.BLINK_CONSECUTIVE_FRAMES) {
                  setBlinkDetected(true);
                  setLastEyeState('open');
                  setLivenessProgress(100);
                  setLivenessStep('complete');
                  setClosedFrameCount(0);
                  
                  toast.success('✓ Verifikasi kehidupan berhasil!', {
                    description: 'Kedipan terdeteksi. Anda terverifikasi sebagai orang asli.',
                    duration: 3000
                  });
                } else {
                  setLastEyeState('open');
                  setClosedFrameCount(0);
                  setLivenessProgress(0);
                }
              } else if (avgEAR >= FACE_CONFIG.BLINK_OPEN_THRESHOLD) {
                setLastEyeState('open');
                if (closedFrameCount > 0 && closedFrameCount < FACE_CONFIG.BLINK_CONSECUTIVE_FRAMES) {
                  setClosedFrameCount(0);
                  setLivenessProgress(0);
                }
              }
            }
            
            // Step 2: Head Turn Detection (Alternative Method)
            // Valid if EITHER left OR right turn is detected (tidak perlu keduanya)
            if (livenessStep === 'turn_left' || livenessStep === 'turn_right') {
              setCurrentYaw(headYaw);
              
              // Turn LEFT detection (negative yaw) - immediately complete
              if (livenessStep === 'turn_left') {
                if (headYaw < -FACE_CONFIG.HEAD_TURN_MIN_ANGLE) {
                  setTurnLeftDetected(true);
                  setHeadTurnDetected(true);
                  setLivenessStep('complete');
                  setLivenessProgress(100);
                  toast.success('✓ Toleh kiri terdeteksi!', {
                    description: 'Verifikasi berhasil - Anda terverifikasi sebagai orang asli.',
                    duration: 3000
                  });
                }
                // Also accept right turn if user turns right instead
                if (headYaw > FACE_CONFIG.HEAD_TURN_MIN_ANGLE) {
                  setTurnRightDetected(true);
                  setHeadTurnDetected(true);
                  setLivenessStep('complete');
                  setLivenessProgress(100);
                  toast.success('✓ Toleh kanan terdeteksi!', {
                    description: 'Verifikasi berhasil - Anda terverifikasi sebagai orang asli.',
                    duration: 3000
                  });
                }
              }
              
              // Turn RIGHT detection (positive yaw) - if somehow step is turn_right
              if (livenessStep === 'turn_right') {
                if (headYaw > FACE_CONFIG.HEAD_TURN_MIN_ANGLE) {
                  setTurnRightDetected(true);
                  setHeadTurnDetected(true);
                  setLivenessStep('complete');
                  setLivenessProgress(100);
                  toast.success('✓ Verifikasi gerakan kepala berhasil!', {
                    description: 'Anda terverifikasi sebagai orang asli.',
                    duration: 3000
                  });
                }
                // Also accept left turn
                if (headYaw < -FACE_CONFIG.HEAD_TURN_MIN_ANGLE) {
                  setTurnLeftDetected(true);
                  setHeadTurnDetected(true);
                  setLivenessStep('complete');
                  setLivenessProgress(100);
                  toast.success('✓ Toleh kiri terdeteksi!', {
                    description: 'Verifikasi berhasil - Anda terverifikasi sebagai orang asli.',
                    duration: 3000
                  });
                }
              }
            }
            
            // Draw liveness status based on current step
            ctx.fillStyle = '#3b82f6';
            ctx.font = 'bold 14px sans-serif';
            let statusText = '';
            if (livenessStep === 'blink') {
              statusText = avgEAR < FACE_CONFIG.BLINK_THRESHOLD ? '👁️ Mata tertutup!' : '👁️ Kedipkan mata...';
            } else if (livenessStep === 'turn_left' || livenessStep === 'turn_right') {
              // Show appropriate message based on current yaw
              if (headYaw < -FACE_CONFIG.HEAD_TURN_MIN_ANGLE) {
                statusText = '✓ Kiri OK!';
              } else if (headYaw > FACE_CONFIG.HEAD_TURN_MIN_ANGLE) {
                statusText = '✓ Kanan OK!';
              } else {
                statusText = '↔️ Toleh kiri/kanan...';
              }
            }
            ctx.fillText(statusText, box.x, box.y - 10);
            
            // Draw debug info
            ctx.fillStyle = '#6366f1';
            ctx.font = '11px sans-serif';
            if (livenessStep === 'blink') {
              ctx.fillStyle = avgEAR < FACE_CONFIG.BLINK_THRESHOLD ? '#10b981' : '#6366f1';
              ctx.fillText(`EAR: ${avgEAR.toFixed(2)}`, box.x, box.y + box.height + 15);
            } else {
              ctx.fillStyle = Math.abs(headYaw) > FACE_CONFIG.HEAD_TURN_MIN_ANGLE ? '#10b981' : '#6366f1';
              ctx.fillText(`Yaw: ${headYaw.toFixed(1)}°`, box.x, box.y + box.height + 15);
            }
          }
        } else if (detections.length > 1) {
          setFaceDetected(false);
          // Draw red boxes for multiple faces
          detections.forEach(det => {
            const box = det.detection.box;
            ctx.strokeStyle = '#ef4444';
            ctx.lineWidth = 2;
            ctx.strokeRect(box.x, box.y, box.width, box.height);
          });
        } else {
          setFaceDetected(false);
        }
      } catch (error) {
        console.error('Face detection error:', error);
      }
    }, FACE_CONFIG.DETECTION_INTERVAL); // Faster detection for better blink capture
  }, [modelsLoaded, livenessEnabled, livenessStep, lastEyeState, headPositions, closedFrameCount, calculateEAR, calculateHeadYaw]);

  // Capture photo and extract face descriptor
  const captureAndProcess = async () => {
    if (!videoRef.current || !canvasRef.current || !faceDetected) {
      toast.error('Pastikan wajah terdeteksi dengan jelas');
      return;
    }
    
    // For verification mode, require liveness check first
    if (mode === 'verify' && livenessEnabled && livenessStep !== 'complete') {
      if (!livenessStep) {
        // Start liveness check
        startLivenessCheck();
        toast.info('🔒 Verifikasi Anti-Spoofing', {
          description: 'Kedipkan mata Anda untuk membuktikan ini bukan foto'
        });
        return;
      } else {
        toast.warning('Selesaikan verifikasi kehidupan terlebih dahulu', {
          description: livenessStep === 'blink' ? 'Kedipkan mata Anda' : 'Gerakkan kepala ke kiri atau kanan'
        });
        return;
      }
    }

    setDetecting(true);

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Mirror for selfie camera
      if (facingMode === 'user') {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }

      ctx.drawImage(video, 0, 0);

      // Get photo as base64
      const photoBase64 = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedPhoto(photoBase64);

      // Detect face and get descriptor
      const detections = await faceapi
        .detectSingleFace(canvas, new faceapi.TinyFaceDetectorOptions({
          inputSize: 416,
          scoreThreshold: FACE_CONFIG.MIN_CONFIDENCE
        }))
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (!detections) {
        toast.error('Tidak dapat mendeteksi wajah. Coba lagi dengan pencahayaan lebih baik.');
        setCapturedPhoto(null);
        setDetecting(false);
        return;
      }

      const descriptor = Array.from(detections.descriptor);

      if (mode === 'register') {
        // Registration mode - return descriptor
        stopCamera();
        onRegistrationComplete?.({
          photo: photoBase64,
          descriptor: descriptor
        });
        toast.success('Wajah berhasil direkam! 📸');
      } else {
        // Verification mode - compare with reference
        await verifyFace(descriptor);
      }
    } catch (error) {
      console.error('Capture error:', error);
      toast.error('Gagal memproses foto wajah');
      setCapturedPhoto(null);
    } finally {
      setDetecting(false);
    }
  };

  // Verify face against reference
  const verifyFace = async (capturedDescriptor) => {
    if (!referenceDescriptor || referenceDescriptor.length !== 128) {
      toast.error('Data wajah referensi tidak valid');
      setVerificationResult({ matched: false, reason: 'invalid_reference' });
      return;
    }

    setVerifying(true);

    try {
      // Calculate Euclidean distance between descriptors
      const distance = faceapi.euclideanDistance(
        new Float32Array(capturedDescriptor),
        new Float32Array(referenceDescriptor)
      );

      const matched = distance < FACE_CONFIG.MATCH_THRESHOLD;
      const confidence = Math.max(0, Math.min(100, (1 - distance) * 100));

      const result = {
        matched,
        distance,
        confidence,
        threshold: FACE_CONFIG.MATCH_THRESHOLD
      };

      setVerificationResult(result);

      if (matched) {
        toast.success(`Wajah terverifikasi! (${confidence.toFixed(0)}% cocok)`, {
          icon: <ShieldCheck className="w-5 h-5 text-emerald-600" />
        });
        stopCamera();
        
        // Include liveness status in verification result
        const livenessSkipped = livenessEnabled && !(blinkDetected || headTurnDetected);
        const livenessMethod_used = livenessMethod;
        
        onVerificationComplete?.({ 
          success: true, 
          ...result,
          liveness_verified: blinkDetected || headTurnDetected,
          liveness_skipped: livenessSkipped,
          liveness_method: livenessMethod_used
        });
      } else {
        toast.error('Wajah tidak cocok dengan data referensi', {
          icon: <ShieldAlert className="w-5 h-5 text-red-600" />
        });
        // Allow retry
        setCapturedPhoto(null);
        setVerificationResult(null);
      }
    } catch (error) {
      console.error('Verification error:', error);
      toast.error('Gagal memverifikasi wajah');
    } finally {
      setVerifying(false);
    }
  };

  // Verify from provided photo (for AbsensiPage integration)
  const verifyFromPhoto = useCallback(async (photoBase64) => {
    if (!modelsLoaded || !referenceDescriptor) return;

    setVerifying(true);
    
    try {
      // Create image element from base64
      const img = await faceapi.fetchImage(photoBase64);
      
      const detections = await faceapi
        .detectSingleFace(img, new faceapi.TinyFaceDetectorOptions({
          inputSize: 416,
          scoreThreshold: FACE_CONFIG.MIN_CONFIDENCE
        }))
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (!detections) {
        onVerificationComplete?.({ 
          success: false, 
          reason: 'no_face_detected',
          message: 'Tidak ada wajah terdeteksi dalam foto'
        });
        return;
      }

      const capturedDescriptor = Array.from(detections.descriptor);
      await verifyFace(capturedDescriptor);
    } catch (error) {
      console.error('Photo verification error:', error);
      onVerificationComplete?.({ 
        success: false, 
        reason: 'error',
        message: 'Gagal memverifikasi foto'
      });
    } finally {
      setVerifying(false);
    }
  }, [modelsLoaded, referenceDescriptor]);

  // Effect to verify from currentPhoto if provided
  useEffect(() => {
    if (currentPhoto && modelsLoaded && referenceDescriptor && mode === 'verify') {
      verifyFromPhoto(currentPhoto);
    }
  }, [currentPhoto, modelsLoaded, referenceDescriptor, mode, verifyFromPhoto]);

  // Initialize camera when models loaded (for camera-based modes)
  useEffect(() => {
    if (modelsLoaded && !currentPhoto) {
      initCamera();
    }
    return () => stopCamera();
  }, [modelsLoaded, currentPhoto]);

  // Render loading state
  if (!modelsLoaded) {
    return (
      <Card className="border-indigo-100/50">
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            <Scan className="w-12 h-12 mx-auto text-indigo-600 animate-pulse" />
            <div>
              <p className="font-medium text-indigo-950">Memuat Sistem Verifikasi Wajah</p>
              <p className="text-sm text-gray-500 mt-1">Mohon tunggu sebentar...</p>
            </div>
            <Progress value={loadingProgress} className="w-full max-w-xs mx-auto" />
            {loadingError && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{loadingError}</AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Render verification from photo (no camera UI)
  if (currentPhoto && mode === 'verify') {
    return (
      <Card className="border-indigo-100/50">
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            {verifying ? (
              <>
                <Loader2 className="w-12 h-12 mx-auto text-indigo-600 animate-spin" />
                <p className="font-medium">Memverifikasi wajah...</p>
              </>
            ) : verificationResult ? (
              verificationResult.matched ? (
                <>
                  <ShieldCheck className="w-12 h-12 mx-auto text-emerald-600" />
                  <p className="font-medium text-emerald-700">Wajah Terverifikasi!</p>
                  <p className="text-sm text-gray-500">
                    Kecocokan: {verificationResult.confidence.toFixed(0)}%
                  </p>
                </>
              ) : (
                <>
                  <ShieldAlert className="w-12 h-12 mx-auto text-red-600" />
                  <p className="font-medium text-red-700">Wajah Tidak Cocok</p>
                  <Button onClick={() => onCancel?.()} variant="outline" className="mt-4">
                    Coba Lagi
                  </Button>
                </>
              )
            ) : null}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Render camera-based UI
  return (
    <Card className="border-indigo-100/50">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          {mode === 'register' ? (
            <>
              <UserCheck className="w-5 h-5 text-indigo-600" />
              Daftarkan Wajah Anda
            </>
          ) : (
            <>
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              Verifikasi Wajah
            </>
          )}
        </CardTitle>
        <CardDescription>
          {mode === 'register' 
            ? 'Posisikan wajah Anda di tengah bingkai dengan pencahayaan yang baik'
            : 'Verifikasi wajah untuk melanjutkan absensi'
          }
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Camera Preview */}
        <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
          />
          
          {/* Face detection overlay */}
          <canvas
            ref={overlayCanvasRef}
            className="absolute inset-0 w-full h-full"
            style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
          />
          
          {/* Loading overlay */}
          {!cameraReady && !cameraError && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70">
              <div className="text-center text-white">
                <Loader2 className="w-10 h-10 animate-spin mx-auto mb-2" />
                <p className="text-sm">Memuat kamera...</p>
              </div>
            </div>
          )}
          
          {/* Camera Error Overlay */}
          {cameraError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-red-900/90 to-red-800/90 p-4">
              <div className="text-center text-white max-w-sm">
                <div className="w-16 h-16 mx-auto mb-4 bg-red-500/30 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-10 h-10 text-red-300" />
                </div>
                <h3 className="text-lg font-bold mb-2">{cameraError.title}</h3>
                <p className="text-red-200 text-sm mb-4">{cameraError.message}</p>
                <div className="bg-white/10 rounded-lg p-3 text-left mb-4">
                  <p className="text-xs text-red-200 font-medium mb-1">💡 Solusi:</p>
                  <p className="text-sm text-white">{cameraError.solution}</p>
                </div>
                <div className="flex gap-2 justify-center">
                  <Button
                    size="sm"
                    onClick={() => {
                      setCameraError(null);
                      initCamera();
                    }}
                    className="bg-white text-red-700 hover:bg-red-100"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Coba Lagi
                  </Button>
                  {onCancel && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={onCancel}
                      className="text-white hover:bg-white/10"
                    >
                      Batal
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Face detection status badge */}
          <div className="absolute top-3 left-3">
            <Badge 
              variant={faceDetected ? "default" : "secondary"}
              className={faceDetected 
                ? "bg-emerald-500 hover:bg-emerald-500" 
                : "bg-gray-500 hover:bg-gray-500"
              }
            >
              {faceDetected ? (
                <>
                  <Check className="w-3 h-3 mr-1" />
                  Wajah Terdeteksi
                </>
              ) : (
                <>
                  <Scan className="w-3 h-3 mr-1" />
                  Mencari Wajah...
                </>
              )}
            </Badge>
          </div>
          
          {/* Camera switch button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-3 right-3 bg-black/50 text-white hover:bg-black/70"
            onClick={() => {
              stopCamera();
              setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
              setTimeout(initCamera, 300);
            }}
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
          
          {/* Guide overlay */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div 
              className={`w-48 h-48 border-4 rounded-full transition-colors ${
                faceDetected ? 'border-emerald-500/70' : 'border-white/30'
              }`}
              style={{ borderStyle: 'dashed' }}
            />
          </div>
        </div>
        
        {/* Hidden canvas for capture */}
        <canvas ref={canvasRef} className="hidden" />
        
        {/* Liveness Detection Progress (for verify mode) */}
        {mode === 'verify' && livenessEnabled && livenessStep && livenessStep !== 'complete' && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
            {/* BLINK DETECTION UI */}
            {livenessStep === 'blink' && (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    lastEyeState === 'closed' ? 'bg-green-100 animate-pulse' : 'bg-blue-100 animate-pulse'
                  }`}>
                    <Eye className={`w-5 h-5 ${lastEyeState === 'closed' ? 'text-green-600' : 'text-blue-600'}`} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-blue-900">
                      {lastEyeState === 'closed' ? '✓ Mata tertutup terdeteksi!' : '👁️ Kedipkan Mata Anda'}
                    </p>
                    <p className="text-sm text-blue-700">
                      {lastEyeState === 'closed' 
                        ? 'Bagus! Sekarang buka mata Anda kembali...'
                        : 'Berkedip perlahan - tutup mata selama ~0.5 detik, lalu buka'
                      }
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Progress value={livenessProgress} className="h-2" />
                  <div className="flex justify-between text-xs text-blue-600">
                    <span>
                      {livenessProgress === 0 ? 'Menunggu kedipan...' : 
                       livenessProgress === 50 ? 'Mata tertutup! Buka mata...' : 
                       'Memverifikasi...'}
                    </span>
                    <span className="font-mono">EAR: {currentEAR.toFixed(2)}</span>
                  </div>
                </div>
                {/* Tips for better detection */}
                <div className="mt-3 p-2 bg-amber-50 rounded border border-amber-200">
                  <p className="text-xs text-amber-800">
                    💡 <strong>Tips:</strong> Kedipkan dengan perlahan dan pastikan pencahayaan cukup. 
                    Jika sulit terdeteksi, coba kedipkan lebih lama (~0.5-1 detik).
                  </p>
                </div>
                
                {/* Alternative Option - shown after failed attempts */}
                {showAlternativeOption && (
                  <div className="mt-3 p-3 bg-indigo-100 rounded-lg border border-indigo-300">
                    <p className="text-sm text-indigo-800 font-medium mb-2">
                      🔄 Kesulitan mengedipkan mata?
                    </p>
                    <Button
                      size="sm"
                      onClick={switchToHeadTurnMethod}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid="switch-head-turn-btn"
                    >
                      <MoveHorizontal className="w-4 h-4 mr-2" />
                      Gunakan Metode Toleh Kepala
                    </Button>
                  </div>
                )}
                
                {/* Skip Liveness Button - appears after countdown */}
                <div className="mt-3">
                  {canSkipLiveness ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={skipLivenessCheck}
                      className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="skip-liveness-btn"
                    >
                      <MoveHorizontal className="w-4 h-4 mr-2" />
                      Lewati Verifikasi Kedip
                    </Button>
                  ) : skipCountdown !== null && skipCountdown > 0 ? (
                    <p className="text-xs text-center text-gray-500">
                      Tombol lewati akan muncul dalam {skipCountdown} detik...
                    </p>
                  ) : null}
                </div>
              </>
            )}
            
            {/* HEAD TURN DETECTION UI */}
            {(livenessStep === 'turn_left' || livenessStep === 'turn_right') && (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    (turnLeftDetected || turnRightDetected)
                      ? 'bg-green-100' : 'bg-blue-100 animate-pulse'
                  }`}>
                    <MoveHorizontal className={`w-5 h-5 ${
                      (turnLeftDetected || turnRightDetected)
                        ? 'text-green-600' : 'text-blue-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-blue-900">
                      ↔️ Tolehkan Kepala ke KIRI atau KANAN
                    </p>
                    <p className="text-sm text-blue-700">
                      Putar kepala Anda ke salah satu arah hingga terdeteksi
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Progress value={livenessProgress} className="h-2" />
                  <div className="flex justify-between text-xs text-blue-600">
                    <span>
                      {(turnLeftDetected || turnRightDetected) ? 'Terdeteksi! ✓' : 'Tolehkan ke kiri atau kanan...'}
                    </span>
                    <span className="font-mono">Yaw: {currentYaw.toFixed(1)}°</span>
                  </div>
                </div>
                {/* Visual guide for head turn */}
                <div className="mt-3 flex justify-center gap-4">
                  <div className={`p-2 rounded-lg ${turnLeftDetected ? 'bg-green-100 border-green-300' : 'bg-gray-100 border-gray-200'} border`}>
                    <span className="text-2xl">{turnLeftDetected ? '✅' : '👈'}</span>
                    <p className="text-xs text-center mt-1">Kiri</p>
                  </div>
                  <div className="p-2 text-gray-400 text-lg">atau</div>
                  <div className={`p-2 rounded-lg ${turnRightDetected ? 'bg-green-100 border-green-300' : 'bg-gray-100 border-gray-200'} border`}>
                    <span className="text-2xl">{turnRightDetected ? '✅' : '👉'}</span>
                    <p className="text-xs text-center mt-1">Kanan</p>
                  </div>
                </div>
                {/* Skip option for head turn */}
                <div className="mt-3">
                  {canSkipLiveness && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={skipLivenessCheck}
                      className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="skip-liveness-btn"
                    >
                      <MoveHorizontal className="w-4 h-4 mr-2" />
                      Lewati Verifikasi
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        
        {/* Liveness Complete Badge */}
        {mode === 'verify' && livenessEnabled && livenessStep === 'complete' && (
          <Alert className={(blinkDetected || headTurnDetected) ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}>
            <ShieldCheck className={`w-4 h-4 ${(blinkDetected || headTurnDetected) ? 'text-emerald-600' : 'text-amber-600'}`} />
            <AlertDescription className={(blinkDetected || headTurnDetected) ? "text-emerald-800" : "text-amber-800"}>
              {blinkDetected ? (
                <>
                  <span className="font-semibold">✓ Verifikasi Kedipan Berhasil!</span>
                  <br />
                  <span className="text-sm">Kedipan terdeteksi. Klik tombol untuk melanjutkan verifikasi wajah.</span>
                </>
              ) : headTurnDetected ? (
                <>
                  <span className="font-semibold">✓ Verifikasi Gerakan Kepala Berhasil!</span>
                  <br />
                  <span className="text-sm">Gerakan kepala terdeteksi. Klik tombol untuk melanjutkan verifikasi wajah.</span>
                </>
              ) : (
                <>
                  <span className="font-semibold">⚠️ Verifikasi Dilewati</span>
                  <br />
                  <span className="text-sm">Melanjutkan tanpa verifikasi kehidupan. Klik tombol untuk verifikasi wajah.</span>
                </>
              )}
            </AlertDescription>
          </Alert>
        )}
        
        {/* Instructions */}
        <Alert className={faceDetected ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}>
          <AlertDescription className={faceDetected ? "text-emerald-800" : "text-amber-800"}>
            {faceDetected ? (
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                {mode === 'verify' && livenessEnabled && livenessStep !== 'complete'
                  ? 'Wajah terdeteksi! Ikuti instruksi di atas untuk verifikasi keamanan.'
                  : `Wajah terdeteksi! Klik tombol di bawah untuk ${mode === 'register' ? 'mendaftar' : 'verifikasi'}.`
                }
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Posisikan wajah Anda di dalam lingkaran dengan pencahayaan yang baik.
              </span>
            )}
          </AlertDescription>
        </Alert>
        
        {/* Action buttons */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => {
              stopCamera();
              setLivenessStep(null);
              setLivenessProgress(0);
              setLivenessMethod('head_turn'); // Reset to default head_turn
              setShowAlternativeOption(false);
              setBlinkFailCount(0);
              onCancel?.();
            }}
            className="flex-1"
          >
            <X className="w-4 h-4 mr-2" />
            Batal
          </Button>
          
          {/* Main action button - show different states based on liveness */}
          {mode === 'verify' && livenessEnabled && livenessStep === 'complete' ? (
            // Liveness complete - show Verifikasi Wajah button directly
            <Button
              onClick={captureAndProcess}
              disabled={!faceDetected || detecting || verifying}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              data-testid="verify-face-btn"
            >
              {detecting || verifying ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Camera className="w-4 h-4 mr-2" />
              )}
              Verifikasi Wajah
            </Button>
          ) : (
            // During liveness check or other modes
            <Button
              onClick={captureAndProcess}
              disabled={!faceDetected || detecting || verifying}
              className={`flex-1 ${
                mode === 'verify' && livenessEnabled && livenessStep !== 'complete'
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : mode === 'register' 
                    ? 'bg-indigo-950' 
                    : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              {detecting || verifying ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : mode === 'verify' && livenessEnabled && !livenessStep ? (
                <ShieldCheck className="w-4 h-4 mr-2" />
              ) : mode === 'verify' && livenessEnabled && livenessStep !== 'complete' ? (
                livenessStep === 'blink' ? <Eye className="w-4 h-4 mr-2" /> : <MoveHorizontal className="w-4 h-4 mr-2" />
              ) : (
                <Camera className="w-4 h-4 mr-2" />
              )}
              {mode === 'verify' && livenessEnabled && !livenessStep
                ? 'Mulai Verifikasi'
                : mode === 'verify' && livenessEnabled && livenessStep !== 'complete'
                  ? 'Selesaikan Verifikasi'
                  : mode === 'register' 
                    ? 'Daftarkan Wajah' 
                    : 'Verifikasi Wajah'
              }
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FaceVerification;
